import React, { useCallback, useMemo, useState } from 'react'
import { Button, Flex, notification, Popconfirm } from 'antd'
import {
  useDeleteWorkableEntityConfigMutation,
  useGetWorkableEntityConfigsQuery,
} from '@/features/portal-configuration/portal-configuration.api.ts'
import { CustomAgGrid } from '@/common/components/CustomAgGrid.tsx'
import { WorkableEntityConfig } from '@/features/portal-configuration/workable-configuration.def.ts'
import { GridOptions } from 'ag-grid-community'
import { handleApiError } from '@/utils/api.tsx'
import { WorkableEntityConfigResponseItem } from '@/features/portal-configuration/workable-configuration-request.def.ts'
import { EditWorkableConfigDrawer } from '@/pages/portal-configuration/components/EditWorkableConfigDrawer.tsx'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'

export const WorkableApiConfigTab: React.FC = () => {
  const { data, refetch, isFetching } = useGetWorkableEntityConfigsQuery()
  const [deleteConfig] = useDeleteWorkableEntityConfigMutation()
  const [targetConfig, setTargetConfig] = useState<WorkableEntityConfigResponseItem>()
  const [drawerOpen, setDrawerOpen] = useState(false)

  const handleDelete = useCallback(async (id: number) => {
    const result = await deleteConfig({ id })
    if ('data' in result) {
      notification.success({ message: 'Configuration deleted successfully' })
    } else if ('error' in result) {
      handleApiError(result.error)
    }
  }, [])

  const columnDefs: GridOptions<WorkableEntityConfigResponseItem>['columnDefs'] = useMemo(() => [
    {
      headerName: 'Country',
      field: 'country',
      colId: 'country',
    },
    {
      headerName: 'Member Name',
      field: 'name',
      colId: 'name',
    },
    {
      headerName: 'Sub-Domain',
      field: 'subdomain',
      colId: 'subdomain',
    },
    {
      headerName: 'Actions',
      colId: 'actions',
      filter: false,
      cellRenderer: ({ data }: { data: WorkableEntityConfig }) => {
        if (!data) return null
        return (
          <Flex gap="small" align="center" className="h-full">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setTargetConfig(data)
                setDrawerOpen(true)
              }}
            />
            <Popconfirm
              title="Are you sure?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => handleDelete(data.id)}
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Flex>
        )
      },
    },
  ], [])

  return (
    <Flex vertical gap="middle">
      <CustomAgGrid<WorkableEntityConfigResponseItem>
        rowData={data || []}
        columnDefs={columnDefs}
        refetch={refetch}
        isLoading={isFetching}
        gridOptions={{
          rowSelection: undefined,
        }}
        toolbarExtra={<Button type="primary" onClick={() => setDrawerOpen(true)}>Add New Config</Button>}
      />
      <EditWorkableConfigDrawer
        key={targetConfig?.id}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false)
          setTargetConfig(undefined)
        }}
        config={targetConfig}
        onSuccess={refetch}
      />
    </Flex>
  )
}